import { useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Card, DataTable, Select, type ColumnDef, type SelectOption } from "@/deps/ui-library";
import { useCalendar, useFiscalYears } from "@/api/queries";
import { monthLabel, monthOf, monthRange, monthsOf } from "@/lib/calendar";
import { cn } from "@/lib/cn";
import { today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { DateField as DateInput, DateRangeField } from "@/components/DateField";

/** A statement's row: a heading, a line, a subtotal or the total, with figures to its right. */
export interface StatementRow {
  label: string;
  kind: "header" | "line" | "subtotal" | "total";
  values: Array<string | null>;
}

/** A table of a statement, whose headings are rows with no figures, so none can be read as a zero. */
export function StatementTable({
  caption,
  columns,
  rows,
  loading,
}: {
  caption: string;
  columns: string[];
  rows: StatementRow[];
  loading: boolean;
}) {
  const defs = useMemo<Array<ColumnDef<StatementRow>>>(
    () => [
      {
        id: "label",
        header: columns[0] ?? "",
        cell: ({ row: { original: row } }) => (
          <span
            className={cn(
              "block",
              row.kind === "line" && "pl-4",
              row.kind === "header" && "text-fg-muted font-medium",
            )}
          >
            {row.label}
          </span>
        ),
      },
      ...columns.slice(1).map((header, index): ColumnDef<StatementRow> => ({
        id: `v${index}`,
        header,
        meta: { textAlign: "right", numeric: true },
        cell: ({ row: { original: row } }) => {
          const value = row.values[index];
          return value === null || value === undefined ? "" : formatAmount(value);
        },
      })),
    ],
    [columns],
  );
  return (
    <Card size="none" className="overflow-hidden">
      <DataTable
        aria-label={caption}
        size="sm"
        enableSorting={false}
        columns={defs}
        data={rows}
        getRowId={(row, index) => `${row.label}-${index}`}
        rowClassName={({ original: row }) =>
          cn(
            row.kind === "total" && "bg-muted font-semibold",
            row.kind === "subtotal" && "font-medium",
          )
        }
        loading={loading}
        emptyContent="Nothing to report for this period."
        pagination={{ mode: "manual", show: false }}
        containerClassName="rounded-none border-0"
      />
    </Card>
  );
}

export function chosen(details: { value: SelectOption | SelectOption[] | null }): string {
  const option = Array.isArray(details.value) ? details.value[0] : details.value;
  return option ? String(option.value) : "";
}

/** A date held in the address bar, so a report at a date is a link somebody can send. */
export function useDateParam(route: string, name: string, fallback: string) {
  const search = useSearch({ strict: false }) as Record<string, string | undefined>;
  const navigate = useNavigate();
  return [
    search[name] ?? fallback,
    (value: string) =>
      void navigate({
        to: route as never,
        search: { ...search, [name]: value } as never,
        replace: true,
      }),
  ] as const;
}

export function DateField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <DateInput
      label={label}
      value={value}
      onChange={(next) => {
        if (next) onChange(next);
      }}
    />
  );
}

/**
 * A report's from–to pair as one range field. One bound changes at a time, and an emptied bound
 * keeps what the report reads, so the report never asks for half a period.
 */
export function PeriodField({
  from,
  to,
  onFrom,
  onTo,
}: {
  from: string;
  to: string;
  onFrom: (value: string) => void;
  onTo: (value: string) => void;
}) {
  return (
    <DateRangeField
      label="Period"
      variant="input"
      clearable={false}
      value={{ from, to }}
      onChange={(range) => {
        if (range.from && range.from !== from) onFrom(range.from);
        else if (range.to && range.to !== to) onTo(range.to);
      }}
    />
  );
}

export { monthLabel, monthRange };

/** A month of the fiscal year, for the tax reports, held in the address bar. */
export function useMonthParam(route: string) {
  const search = useSearch({ strict: false }) as { month?: string };
  const navigate = useNavigate();
  const { data: years } = useFiscalYears();
  const now = today();
  const year =
    years?.find((y) => y.start <= now && now <= y.end) ?? years?.find((y) => y.isCurrent);
  const calendar = useCalendar();
  const thisMonth = monthOf(now, calendar);
  const months = year ? monthsOf(year, calendar) : [thisMonth];
  const month = search.month ?? thisMonth;
  return {
    month,
    months: months.includes(month) ? months : [month, ...months],
    setMonth: (value: string) =>
      void navigate({ to: route as never, search: { month: value } as never, replace: true }),
    ...monthRange(month),
  };
}

export function MonthField({
  month,
  months,
  onChange,
}: {
  month: string;
  months: string[];
  onChange: (value: string) => void;
}) {
  return (
    <div className="w-44">
      <Select
        label="Month"
        options={months.map((m) => ({ value: m, label: monthLabel(m) }))}
        value={month}
        onChange={(details) => {
          const picked = chosen(details);
          if (picked) onChange(picked);
        }}
        showClearButton={false}
      />
    </div>
  );
}

export function Figure({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail?: string;
}) {
  return (
    <div className="border-line bg-surface flex min-w-44 flex-1 flex-col gap-1 rounded-lg border p-3">
      <span className="text-fg-muted text-xs">{label}</span>
      <span className="num text-fg text-lg font-semibold">{formatAmount(value)}</span>
      {detail ? <span className="text-fg-muted text-xs">{detail}</span> : null}
    </div>
  );
}
